import { MONEY_SCALE, type QuotationDetailDto, describe } from "@hisense/shared";
import { printHtml } from "./printWindow";

const esc = (s: string) =>
  s.replace(
    /[&<>"]/g,
    (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!,
  );

const printDate = (ms: number | null | undefined) => {
  if (!ms) return "N/A";
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const major4 = (minor: number) => (minor / MONEY_SCALE).toFixed(4);

const absUrl = (url: string | null | undefined): string | null => {
  const v = (url ?? "").trim();
  if (!v) return null;
  if (/^(https?:|data:|blob:)/i.test(v)) return v;
  const origin = window.location.origin;
  return `${origin}${v.startsWith("/") ? "" : "/"}${v}`;
};

const leadTime = (v: string | null | undefined) => {
  const s = (v ?? "").trim();
  if (!s || s === "N/A") return "N/A";
  if (/day/i.test(s)) return esc(s);
  return `${esc(s)} days`;
};

const th = (
  label: string,
  width: string,
  align: "center" | "left" = "center",
) =>
  `<th style="background:#000 !important;color:#fff !important;font-weight:bold;padding:8px 4px;text-align:${align};border:1px solid #000;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;width:${width};">${label}</th>`;

/**
 * Commercial quotation print — layout matches generateQuotationPrintHTML in
 * the legacy Quotation.js: A4 landscape, To/Details boxes, product images,
 * independent EXW/DOD/Under-MOQ columns, remarks and saved validity terms.
 */
export function buildQuotationHtml(q: QuotationDetailDto): string {
  const hasDtd = q.items.some((it) => it.dtdPrice != null);
  const hasExw = q.items.some((it) => it.exwPrice != null);
  const hasUnderMoq = q.items.some((it) => it.underMoqPrice != null);
  const logoSrc = `${window.location.origin}/brand/hisense-logo.webp`;
  const customer = q.printCustomer;
  const status =
    q.status === "Issued" && q.validUntil != null && q.validUntil < Date.now()
      ? "Expired"
      : (q.status ?? "Draft");

  const itemRows = q.items
    .map((it, index) => {
      const zebra = index % 2 === 0 ? "#f8f9fa" : "white";
      const img = absUrl(it.imageUrl);
      const imgHtml = img
        ? `<img src="${esc(img)}" alt="" style="width:90px;height:90px;object-fit:cover;border:1px solid #ddd;display:block;" />`
        : "";
      const remarkHtml = it.remark
        ? `<div style="font-size:8px;color:#555;font-style:italic;line-height:1.2;text-align:center;word-break:break-word;width:90px;margin-top:3px;">${esc(it.remark)}</div>`
        : "";
      // Ô giá chỉ có tiền tệ + số tiền. Dòng phụ "per <đơn vị>" đã bỏ theo yêu
      // cầu khách 12/09: đơn vị đã có cột Unit riêng, nhắc lại dưới từng ô giá
      // làm bản in rối mà không thêm thông tin nào.
      const price = (minor: number | null | undefined) =>
        minor != null ? `${esc(it.currency || q.currency)} ${major4(minor)}` : "—";
      return `<tr style="background:${zebra};">
        <td class="c mono b" style="color:#007bff;font-size:9px;">${esc(it.productCode ?? "")}</td>
        <td class="c muted" style="font-size:9px;">${esc(it.subRef || "-")}</td>
        <td style="font-size:9px;line-height:1.2;word-wrap:break-word;">${esc(describe(it, q.printLanguage))}</td>
        <td class="c b" style="font-size:9px;">${esc(it.size ?? "")}</td>
        <td class="c b" style="font-size:9px;">${esc(it.color ?? "")}</td>
        <td class="c b muted" style="font-size:9px;">${esc(it.unit || "pieces")}</td>
        <td class="c b" style="color:#007bff;font-size:9px;">${price(it.unitPrice)}</td>
        ${hasExw ? `<td class="c b" style="font-size:9px;">${price(it.exwPrice)}</td>` : ""}
        ${hasDtd ? `<td class="c b" style="color:#007bff;font-size:9px;">${price(it.dtdPrice)}</td>` : ""}
        <td class="c" style="font-size:9px;">${it.moq != null ? it.moq : "N/A"}</td>
        <td class="c" style="font-size:9px;">${it.mcq != null ? it.mcq : "-"}</td>
        <td class="c" style="font-size:9px;">${it.msq != null ? it.msq : "-"}</td>
        ${hasUnderMoq ? `<td class="c b" style="color:#007bff;font-size:9px;">${price(it.underMoqPrice)}</td>` : ""}
        <td class="c" style="font-size:9px;">${leadTime(it.sampleLeadTime)}</td>
        <td class="c" style="font-size:9px;">${leadTime(it.bulkLeadTime)}</td>
        <td class="c" style="padding:2px;vertical-align:top;">
          <div style="display:flex;align-items:center;justify-content:center;flex-direction:column;gap:6px;">
            ${imgHtml}${remarkHtml}
          </div>
        </td>
      </tr>`;
    })
    .join("");

  const toBox = customer
    ? `<div style="flex:1;border:2px solid #000;padding:10px;border-radius:6px;background:#f8f9fa;">
        <h3 class="box-h">To:</h3>
        <p style="margin:2px 0;font-weight:bold;font-size:12px;">${esc(customer.companyName)}</p>
        ${q.attn ? `<p style="margin:2px 0;font-size:11px;font-style:italic;"><strong>Attn:</strong> ${esc(q.attn)}</p>` : ""}
        <p style="margin:2px 0;font-size:11px;white-space:pre-line;">${esc(customer.address ?? "")}</p>
        <p style="margin:2px 0;font-size:11px;">Tel: ${esc(customer.phone ?? "")}</p>
        <p style="margin:2px 0;font-size:11px;">Email: ${esc(customer.email ?? "")}</p>
      </div>`
    : "";

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${esc(q.quotationNo)}</title>
  <style>
    @page { margin: 10mm; size: A4 landscape; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, sans-serif; margin: 0; padding: 15px; color: #333;
      line-height: 1.4; font-size: 14px; background: white;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .mono { font-family: "Courier New", monospace; }
    .c { text-align: center; } .b { font-weight: bold; } .muted { color: #666; }
    p { margin: 5px 0; }
    /* Cột rộng cố định thì chữ không có chỗ ngắt (Hook)13.5x11mm) tràn sang cột bên — Roary 19/10/2026. Ngắt ở bất kỳ đâu
       khi cần, như bản in PI / hoá đơn. */
    table.items { width: 100%; border-collapse: collapse; border: 2px solid #000; font-size: 10px; table-layout: fixed; overflow-wrap: anywhere; }
    table.items th, table.items td { border: 1px solid #000; padding: 6px 3px; vertical-align: top; }
    table.items th {
      background: #000 !important; color: #fff !important; font-weight: bold;
      padding: 8px 4px; font-size: 10px;
      -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
    }
    .box-h { margin: 0 0 8px; color: #000; font-size: 14px; border-bottom: 1px solid #000; padding-bottom: 3px; }
    @media print {
      body { margin: 0; padding: 10px; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      table.items th { background: #000 !important; color: #fff !important; }
    }
  </style>
</head>
<body>
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:15px;border-bottom:2px solid #000;padding-bottom:10px;">
    <div style="flex:1;">
      <img src="${esc(logoSrc)}" alt="Hisense Button Co., Ltd"
        style="max-height:80px;max-width:300px;object-fit:contain;"
        onerror="this.style.display='none';this.nextElementSibling.style.display='block';" />
      <div style="display:none;padding:15px;border:2px solid #000;max-width:300px;">
        <h2 style="margin:0;font-size:20px;">Hisense Button Co., Ltd</h2>
        <p style="margin:3px 0 0;font-size:12px;">Button Import &amp; Export Specialists</p>
      </div>
    </div>
    <div style="text-align:right;flex:1;">
      <h1 style="margin:0;color:#2563eb;font-size:28px;font-weight:bold;">QUOTATION</h1>
      <p style="margin:5px 0 0;font-size:14px;font-family:'Courier New',monospace;">
        <strong>Quotation No: ${esc(q.quotationNo)}</strong>
        ${q.workflowVersion === 0 ? "" : `<br/><strong>${esc(status)} · Revision ${q.revision ?? 1}</strong>`}
      </p>
      <p style="margin:3px 0 0;font-size:11px;">
        Issue Date: <strong>${printDate(q.issueDate)}</strong> |
        Prepared By: <strong>${esc(q.preparedBy ?? "")}</strong>
      </p>
    </div>
  </div>

  <div style="display:flex;${customer ? "justify-content:space-between;" : "justify-content:flex-end;"}margin-bottom:15px;gap:20px;">
    ${toBox}
    <div style="flex:1;border:2px solid #000;padding:10px;border-radius:6px;background:#f8f9fa;">
      <h3 class="box-h">Details:</h3>
      <p style="margin:2px 0;font-size:12px;"><strong>Issue Date:</strong> ${printDate(q.issueDate)}</p>
      <p style="margin:2px 0;font-size:12px;"><strong>Prepared By:</strong> ${esc(q.preparedBy ?? "")}</p>
      <p style="margin:2px 0;font-size:12px;"><strong>Printing Date:</strong> ${printDate(q.printingDate ?? Date.now())}</p>
    </div>
  </div>

  <table class="items">
    <thead>
      <tr>
        ${th("Item Code", "8%")}
        ${th("Sub Ref", "7%")}
        ${th("Description", "14%", "left")}
        ${th("Size", "9%")}
        ${th("Color", "6%")}
        ${th("Unit", "5%")}
        ${th("Unit Price", "8%")}
        ${hasExw ? th("EXW Price", "8%") : ""}
        ${hasDtd ? th("DTD Price", "8%") : ""}
        ${th("MOQ", "5%")}
        ${th("MCQ", "5%")}
        ${th("MSQ", "5%")}
        ${hasUnderMoq ? th("Under MOQ", "8%") : ""}
        ${th("Sample L/T", "7%")}
        ${th("Bulk L/T", "7%")}
        ${th("Image/Remark", "18%")}
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>

  <div style="margin-top:15px;border:2px solid #000;padding:12px;border-radius:6px;background:white;">
    <h3 style="margin:0 0 8px;font-size:14px;">Remarks:</h3>
    <p style="margin:0;font-size:12px;line-height:1.6;white-space:pre-wrap;word-wrap:break-word;">${esc(q.remarks ?? "")}</p>
  </div>

  ${
    q.validUntil != null || q.validityTerms
      ? `<div style="margin-top:15px;border:1px solid #000;padding:10px 15px;">
    ${q.validUntil != null ? `<p style="margin:0;font-size:11px;"><strong>Valid Until (takes precedence):</strong> ${printDate(q.validUntil)}</p>` : ""}
    ${q.validityTerms ? `<p style="margin:3px 0 0;font-size:11px;white-space:pre-wrap;">${esc(q.validityTerms)}</p>` : ""}
  </div>`
      : ""
  }
</body>
</html>`;

  return html;
}

export function printQuotation(q: QuotationDetailDto) {
  printHtml(buildQuotationHtml(q));
}
